import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

/** The notification centre (`app/api/notifications.py`).
 *
 *  Every row is the signed-in user's own; the server computed who
 *  receives what. Names and titles in `facts` are resolved when the list
 *  is read, never stored, so the text is rendered here from the type's
 *  i18n keys (`notifications.types.<stem>.title`) in the viewer's
 *  language. */

export type NotificationOrg = { slug: string; name: string };

export type NotificationItem = {
  id: string;
  type: string;
  /** i18n stem; `null` for a type the server no longer knows. */
  stem: string | null;
  created_at: string;
  read_at: string | null;
  /** When the user hid it ("Clear"); `null` while shown. Separate from
   *  `read_at`: hiding does not read, restoring does not unread. */
  dismissed_at: string | null;
  /** An SPA path (`/app/...`) or `null`. */
  link: string | null;
  session_id: string | null;
  org: NotificationOrg | null;
  /** Ids and short facts, plus `actor_name` / `session_title` /
   *  `document_title` when the viewer may still see them. */
  facts: Record<string, string | number | boolean | null>;
};

export type NotificationPage = {
  items: NotificationItem[];
  /** Pass as `before` for the next (older) page; `null` on the last. */
  next_cursor: string | null;
  unread_count: number;
};

export type NotificationEmail = "off" | "instant";

export type NotificationPref = {
  type: string;
  stem: string;
  in_app: boolean;
  email: NotificationEmail;
  user_can_disable: boolean;
  email_allowed: boolean;
  admin_type: boolean;
};

export type NotificationPrefs = {
  /** Whether this deployment can send e-mail at all. */
  email_available: boolean;
  types: NotificationPref[];
};

export const notificationKeys = {
  all: ["notifications"] as const,
  count: ["notifications", "count"] as const,
  list: ["notifications", "list"] as const,
  listOf: (includeHidden: boolean) => ["notifications", "list", { includeHidden }] as const,
  prefs: ["notifications", "prefs"] as const,
};

export const NOTIFICATION_PAGE_SIZE = 20;

/** The badge's count. Pushed by the session SSE stream
 *  (`notification.created` / `.changed` invalidate it) and polled every
 *  two minutes while the tab is visible, since the bus keeps no replay. */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.count,
    queryFn: async () => (await api.get<{ count: number }>("/notifications/unread-count")).data,
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
    retry: false,
  });
}

/** The drawer's list, newest first, a page at a time ("Load older"
 *  fetches the next keyset page). Hidden rows only with `includeHidden`. */
export function useNotificationList({
  enabled,
  includeHidden = false,
}: {
  enabled: boolean;
  includeHidden?: boolean;
}) {
  return useInfiniteQuery({
    queryKey: notificationKeys.listOf(includeHidden),
    queryFn: async ({ pageParam }) =>
      (
        await api.get<NotificationPage>("/notifications", {
          params: {
            before: pageParam ?? undefined,
            limit: NOTIFICATION_PAGE_SIZE,
            include_hidden: includeHidden || undefined,
          },
        })
      ).data,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/notifications/${id}/read`);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** Mark every notification read — or only one session's. */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId?: string) =>
      (
        await api.post<{ updated: number }>("/notifications/read-all", null, {
          params: sessionId ? { session_id: sessionId } : undefined,
        })
      ).data,
    onSuccess: (data) => {
      if (data.updated > 0) void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** Hide one notification (`dismiss`) or show it again (`restore`): a
 *  soft hide for this user only. */
export function useSetNotificationHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, hidden }: { id: string; hidden: boolean }) => {
      await api.post(`/notifications/${id}/${hidden ? "dismiss" : "restore"}`);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** "Clear all": hide every shown notification created at or before
 *  `before` — the newest one the drawer showed, so one that arrives while
 *  the user clicks is not swallowed. */
export function useDismissAllNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (before: string) =>
      (
        await api.post<{ updated: number }>("/notifications/dismiss-all", null, {
          params: { before },
        })
      ).data,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useNotificationPrefs() {
  return useQuery({
    queryKey: notificationKeys.prefs,
    queryFn: async () => (await api.get<NotificationPrefs>("/notifications/preferences")).data,
  });
}

export type NotificationPrefChange = { in_app?: boolean; email?: NotificationEmail };

export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: Record<string, NotificationPrefChange>) =>
      (await api.patch<NotificationPrefs>("/notifications/preferences", { prefs })).data,
    onSuccess: (data) => {
      qc.setQueryData(notificationKeys.prefs, data);
    },
  });
}
